import type { GlyphRenderInstruction } from '@meitu/x-design-text';
import type { TextRenderInput, TextRenderSnapshot, TextRenderSpanRange } from '../types';
import { findSpanIndex } from '../utils';

/**
 * 提取列表 marker 中真正影响正文可用宽度的缓存字段。
 *
 * 图形型 marker 的 URL 与颜色只触发重绘；尺寸和自动编号字段会改变 marker geometry，必须令
 * Pipeline 重新生成与 snapshot 同轮的私有排版事实。
 *
 * @param input - 当前 Text Render 输入。
 * @returns 可稳定序列化的列表 marker 布局状态。
 */
const createListMarkerLayoutCacheValue = (input: TextRenderInput): unknown => {
    const marker = input.listMarker;
    if (!marker || marker.kind === 'character') return marker;
    return {
        kind: marker.kind,
        width: marker.width,
        height: marker.height,
        baseContentTextSize: marker.baseContentTextSize,
        scale: marker.scale,
        ...(marker.kind === 'svg'
            ? {
                  autoIncrement: marker.autoIncrement,
                  padZero: marker.padZero,
                  numberFontFamily: marker.numberFontFamily,
                  numberFontSize: marker.numberFontSize,
              }
            : {}),
    };
};

/**
 * 创建只包含排版字段的稳定缓存键。
 *
 * @param input - 已完成字体解析的文字渲染输入。
 * @returns 不受纯绘制字段影响的缓存键。
 */
export const createLayoutCacheKey = (input: TextRenderInput): string =>
    JSON.stringify({
        text: input.text,
        fontFamily: input.fontFamily,
        fontSize: input.fontSize,
        bold: input.bold,
        italic: input.italic,
        lineThrough: input.lineThrough,
        underline: input.underline,
        spans: input.spans.map((span) => ({
            text: span.text,
            fontFamily: span.fontFamily,
            fontSize: span.fontSize,
            bold: span.bold,
            italic: span.italic,
            lineThrough: span.lineThrough,
            underline: span.underline,
        })),
        lineHeightScale: input.lineHeightScale,
        letterSpacing: input.letterSpacing,
        align: input.align,
        writingMode: input.writingMode,
        width: input.width,
        height: input.height,
        noWrap: input.noWrap,
        listMarker: createListMarkerLayoutCacheValue(input),
    });

/**
 * 创建只包含文字颜色的稳定绘制缓存键。
 *
 * @param input - 当前普通文字渲染输入。
 * @returns 可区分顶层与分段颜色的缓存键。
 */
export const createPaintCacheKey = (input: TextRenderInput): string =>
    JSON.stringify({
        colorRgba: input.colorRgba,
        spanColors: input.spans.map((span) => span.colorRgba),
    });

type SpanPaintRange = TextRenderSpanRange & { readonly colorRgba: number };

/**
 * 按 UTF-8 字节偏移读取当前分段颜色。
 *
 * @param ranges - span 字节范围与当前颜色映射。
 * @param byteStart - 当前指令或字形的起始字节偏移。
 * @param fallbackColor - 未命中 span 时使用的顶层颜色。
 * @returns 当前字节位置对应的文字颜色。
 */
const resolveColorForByte = (ranges: readonly SpanPaintRange[], byteStart: number, fallbackColor: number): number => {
    const index = findSpanIndex(ranges, byteStart);
    return index < 0 ? fallbackColor : ranges[index].colorRgba;
};

/**
 * 在不改变指令几何的前提下替换可绘制指令颜色。
 *
 * @param instruction - 当前文字引擎绘制指令。
 * @param colorRgba - 应写入的当前文字颜色。
 * @returns 替换颜色后的指令；无颜色字段的指令保持原引用。
 */
const patchInstructionColor = (instruction: GlyphRenderInstruction, colorRgba: number): GlyphRenderInstruction =>
    instruction.kind === 'path' || instruction.kind === 'delegated' || instruction.kind === 'decorationRect'
        ? { ...instruction, fillRgba: colorRgba }
        : instruction;

/**
 * 复用已有排版几何并为当前文字颜色生成新 snapshot。
 *
 * Q：为什么需要 patchSnapshotColors？
 * A：颜色不影响排版，因此不进入 layout cache key；但文字引擎会把 `fillRgba` 写入 glyph 和绘制指令。
 *   命中布局缓存后必须替换这些颜色，否则后续绘制会继续使用旧 snapshot 中的颜色。
 *
 * @param snapshot - 已由同一排版输入生成的只读 snapshot。
 * @param input - 仅绘制颜色可能变化的当前输入。
 * @returns 共享原几何数据、更新颜色后的新只读 snapshot。
 */
export const patchSnapshotColors = (snapshot: TextRenderSnapshot, input: TextRenderInput): TextRenderSnapshot => {
    // 引擎和 spanRanges 都使用全局 UTF-8 字节偏移，据此把最新 span 颜色映射到每个 glyph。
    const ranges = snapshot.spanRanges.map((range, index) => ({
        ...range,
        colorRgba: input.spans[index]?.colorRgba ?? input.colorRgba,
    }));
    const glyphInfos = Object.freeze(
        snapshot.glyphInfos.map((glyph) => ({
            ...glyph,
            fillRgba: resolveColorForByte(ranges, glyph.start, input.colorRgba),
        })),
    );

    let glyphCursor = 0;

    // path 没有 byteStart，需按指令与 glyph 的顺序取色；decorationRect 不占用 glyph 游标。
    const instructions = Object.freeze(
        snapshot.instructions.map((instruction) => {
            if (instruction.kind === 'decorationRect') {
                return patchInstructionColor(
                    instruction,
                    resolveColorForByte(ranges, instruction.byteStart, input.colorRgba),
                );
            }

            if (instruction.kind === 'delegated') {
                glyphCursor += 1;
                return patchInstructionColor(
                    instruction,
                    resolveColorForByte(ranges, instruction.byteStart, input.colorRgba),
                );
            }

            const glyph = glyphInfos[glyphCursor];
            glyphCursor += 1;
            return glyph ? patchInstructionColor(instruction, glyph.fillRgba) : instruction;
        }),
    );

    return Object.freeze({ ...snapshot, instructions, glyphInfos });
};
